import React, { useMemo, useState } from 'react';

/**
 * Lightweight sortable data table.
 *
 * columns: [{ key, label, align?, width?, format?(v, row), className?(v, row) }]
 * data:    array of row objects
 * defaultSort: key to sort by initially
 * defaultDir:  'asc' | 'desc'
 */
export default function DataTable({
  columns,
  data,
  defaultSort,
  defaultDir = 'desc',
  maxHeight,
  onRowClick,
  rowClassName,
}) {
  const [sortKey, setSortKey] = useState(defaultSort || columns[0]?.key);
  const [sortDir, setSortDir] = useState(defaultDir);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const arr = [...data];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  function clickHeader(key) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  return (
    <div className="dtable-wrap" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
      <table className="dtable">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={() => clickHeader(c.key)}
                style={c.width ? { minWidth: c.width } : undefined}
                className={`${c.align === 'right' ? 'text-right' : ''} ${
                  sortKey === c.key ? `sorted ${sortDir === 'asc' ? 'asc' : ''}` : ''
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => {
            const extra = typeof rowClassName === 'function' ? rowClassName(row) : rowClassName || '';
            return (
              <tr
                key={row.id || row.key || i}
                className={[onRowClick ? 'dtable-row-click' : '', extra].filter(Boolean).join(' ')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => {
                  const v = row[c.key];
                  const cls = c.className ? c.className(v, row) : '';
                  const align = c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : '';
                  return (
                    <td key={c.key} className={`${align} ${cls}`.trim()}>
                      {c.format ? c.format(v, row) : v == null ? '—' : v}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
